import { formatToIsoDate } from '@packages/toolbox';
import { camelCase } from 'lodash';
import isObject from 'lodash/isObject';
import lodashRandom from 'lodash/random';

import {
  Computable,
  ForeignKey,
  HasId,
  ID,
  QuasarTableColumn,
  ScalarKeys,
} from 'SharedDir/declarations/shared';
import { toDate } from 'SharedDir/utils/date-rule';

/**
 * Метод позволяющий создать объект-словарь из массива объектов
 *
 * @param {Array<T>} list
 * @param {VK} valueKey
 * @param {LK} labelKey
 * @returns {Record<T[VK] extends (string | number | symbol) ? T[VK] : never, T[LK]>}
 * @constructor
 */
export const MakeDictionary = <
  V,
  K extends string | number | symbol,
  T extends Record<K, V>,
  VK extends ScalarKeys<T>,
  LK extends keyof T
>(
  list: Array<T>,
  valueKey: VK,
  labelKey: LK
): Record<T[VK] extends string | number | symbol ? T[VK] : never, T[LK]> =>
  Object.fromEntries(list.map((l) => [l[valueKey], l[labelKey]]));

/**
 * Паттерн для проверки номера телефона на валидность
 *
 * Примеры подходящих значений под маску:
 * 8 ### ###-##-##, +7 (###) ### ##-##, +7 ##########, 8##########,
 * 8(###) ###-####, 8 (####) ## ## ##, 8 (##-##) ## ## ##, 8  ( ### )  ### ## ##
 *
 */
export const PHONE_PATTERN =
  /(\+7|8)[- _]*\(?[- _]*(\d{3}[- _]*\)?([- _]*\d){7}|\d\d[- _]*\d\d[- _]*\)?([- _]*\d){6})/g;

/** Регулярное выражение для удаления html тегов
 * @example '<p class="MsoNormal"><b>Офис «Центральный» Телефон: </b><p class="MsoNormal">'
 *
 */
export const htmlReg = /<[^>]*>/g;

/**
 * Убираем в markdown текстах html теги
 *
 * @param str
 * @example '<p class="MsoNormal"><b>Офис «Центральный» Телефон: </b><p class="MsoNormal">'
 *          'Офис «Центральный» Телефон: '
 */
export const removeHtml = (str) => (str ? str.replaceAll(htmlReg, '') : '');

/**
 * Метод-хелпер упрощающий процедуру удаления элемента из массива
 *
 * Модифицирует переданный массив потому что под капотом использует `splice`.
 * @param {Array<T>} list
 * @param {IdType} id
 * @param {IdName} idField
 * @returns {T | null}
 * @protected
 */
export const removeRecordFromList = <IdName extends ID, IdType, T extends HasId<IdName, IdType>>(
  list: Array<T>,
  id: IdType,
  idField: ID = 'id'
): T | null => {
  const index = list.findIndex((l) => l[idField] === id);
  if (index === -1) {
    return null;
  }
  const deleted = list[index];
  list.splice(index, 1);
  return deleted;
};

/**
 * Метод-хелпер упрощающий процедуру замены элемента в массиве
 *
 * Модифицирует переданный массив потому что под капотом использует `splice`.
 *
 * @param {Array<T>} list
 * @param {IdType} id
 * @param {T} newValue
 * @param {ID} idField
 * @returns {T | null}
 */
export function replaceRecordInList<IdName extends ID, IdType, T extends HasId<IdName, IdType>>(
  list: Array<T>,
  id: IdType,
  newValue: T,
  idField: ID = 'id'
): T | null {
  const index = list.findIndex((l) => l[idField] === id);
  if (index === -1) {
    return null;
  }
  const replaced = list[index];
  list.splice(index, 1, newValue);
  return replaced;
}

/**
 * генерирует сличайное число, если  аргумент число или строка,
 * то случайное число будет выбрано из отрезка от 0 до аргумента,
 * если это массив то отрезок проложиться от первого элемента массива до второго
 *
 * @param inputRange
 * @returns
 */
export const random = (inputRange: string | number | Array<number>): number => {
  if (Array.isArray(inputRange) && inputRange.length === 0) {
    throw new Error('Array should have contain at least one element');
  }
  if (
    typeof inputRange === 'number' ||
    typeof inputRange === 'string' ||
    (Array.isArray(inputRange) && inputRange.length === 1)
  ) {
    return lodashRandom(0, +inputRange);
  }

  return lodashRandom(+inputRange[0], +inputRange[1]);
};

export const camelizeObject = <T extends {} = Record<string, any>>(snake: T): T => {
  const camel = {} as T;
  Object.entries(snake).forEach(([name, value]) => {
    if (isObject(value)) {
      value = camelizeObject(value);
    } else if (Array.isArray(value)) {
      value = value.map(camelizeObject);
    }
    camel[camelCase(name)] = value;
  });
  return camel;
};

/**
 * Строка для склонения строки в различные формы
 * в зависимости от числа
 * 1 сотрудник, 2 сотрудника, 5 сотрудников
 *
 * @param n Количество объектов для которых нужно найти склонение
 * @param titles Склонения для 1, 2, 5
 */
export const pluralFormOfString = (n: number, titles: string[]) => {
  if (n % 10 === 1 && n % 100 !== 11) {
    return titles[0];
  }

  const index = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
  return titles[index];
};

/**
 * Хелпер, позволяющий присваивать свойствам-внешним ключам, тип которых определен ка `ForeignKey<ModelName, ForeignKeyType>`
 * и не может быть назначен без плясок с бубном как null или 1, это вызовет гнев тайпскрипта.
 * ```ts
 * interface Record {
 *   id: number;
 *   typeId: ForeignKey<Type>
 * }
 *
 * const foo: Record = {
 *   id: 1,
 *   typeId: 2, // не сработает
 * }
 * ```
 * А вот так можно:
 * ```ts
 * const foo: Record = {
 *   id: 1,
 *   typeId: fk(2),
 * }
 * ```
 * @see ForeignKey
 * @param {V} key
 * @returns {ForeignKey<T, V>}
 */
export function fk<T, V>(key: V) {
  return key as unknown as ForeignKey<T, V>;
}

/**
 * Обрезает текст по количеству переданных символов, обрезается при этом строго
 * до последнего слова (по пробелу) входящего в максимальную длину, которое попадает в длину, чтобы не было обрезанных слов: "Меро..."
 *
 * @param text
 * @param maxLength
 * @param clipSymbol
 * @returns
 */
export function cropTextSmart(text: string, maxLength: number, clipSymbol = '...'): string {
  if (text.length <= maxLength) {
    return text;
  }

  const lastSpaceIndex = text.lastIndexOf(' ', maxLength);

  if (lastSpaceIndex !== -1) {
    return `${text.substring(0, lastSpaceIndex)} ${clipSymbol}`;
  }

  return `${text.substring(0, maxLength)} ${clipSymbol}`;
}

interface ItemWithChildren<T> {
  children?: ItemWithChildren<T>[];
}

/**
 * Преобразование плоского массива в массив с имеющимся parentId во вложенный
 * массив с детьми. Дети ищутся по ключу parentIdKey
 *
 * INPUT:
 * const flatArray = [
 *     { id: 1, parentId: null },
 *     { id: 2, parentId: 1 },
 *     { id: 3, parentId: 1 },
 *     { id: 4, parentId: 2 },
 *     { id: 5, parentId: 2 },
 *     { id: 6, parentId: 3 },
 *     { id: 7, parentId: 5 },
 *     { id: 8, parentId: 5 },
 *     { id: 9, parentId: 6 },
 *     { id: 10, parentId: 6 },
 * ];
 *
 *  OUTPUT: [
 *     {
 *     id: 1,
 *     parentId: null,
 *     children: [
 *       {
 *         id: 2,
 *         parentId: 1,
 *         children: [
 *           { id: 4, parentId: 2 },
 *           { id: 5, parentId: 2 },
 *         ],
 *       },
 *       {
 *         id: 3,
 *         parentId: 1,
 *         children: [
 *           {
 *             id: 6,
 *             parentId: 3,
 *             children: [
 *               { id: 9, parentId: 6 },
 *               { id: 10, parentId: 6 },
 *             ],
 *           },
 *         ],
 *       },
 *     ],
 *   },
 * ]
 * @param flatArray
 * @param parentIdKey
 * @param parentId
 * @returns
 */
export function buildNestedArray<T extends { id: number }, K extends ScalarKeys<T>>(
  flatArray: Array<T>,
  parentIdKey: K = 'parentId' as K,
  parentId: T[K] = null as T[K]
): ItemWithChildren<T>[] {
  const nestedArray: ItemWithChildren<T>[] = [];

  for (let i = 0; i < flatArray.length; i++) {
    const item: ItemWithChildren<T> | T = flatArray[i];

    if (item[parentIdKey] !== parentId) {
      // eslint-disable-next-line no-continue
      continue;
    }

    const children = buildNestedArray(flatArray, parentIdKey, item.id as T[K]);

    if (children.length) {
      (item as ItemWithChildren<T>).children = children;
    }

    nestedArray.push(item as ItemWithChildren<T>);
  }

  return nestedArray;
}

export interface ItemWithParentId {
  id: number;
  parentId: number | null;
}

export type TransformedFlatItem<T> = T & { level: number };
/**
 * Преобразуем плоский массив у которого в свойствах есть ссылка на родителя (parentIdKey),
 * в плоский массив который ОТСОРТИРОВАН по родителю и выставлен уровень (level)
 *
 * INPUT:
 * [{id: 1, parentId: null, name: '1'}, {id: 2, parentId: 1, name: '1.1'}, {id: 3, parentId: 1, name: '1.2'}, {id: 4, parentId: 2, name: '1.1.1'}]
 *
 * OUTPUT:
 * [
 *  {id: 1, parentId: null, name: '1', level: 0},
 *  {id: 2, parentId: 1, name: '1.1', level: 1},
 *  {id: 4, parentId: 2, name: '1.1.1', level: 2},
 *  {id: 3, parentId: 1, name: '1.2', level: 1},
 * ]
 *
 * @param initialArray
 * @returns
 */
export function transformFlatArrayToArrayWithLevel<T extends ItemWithParentId, K extends keyof T>(
  initialArray: Array<T>,
  parentIdKey: K = 'parentId' as K
): Array<TransformedFlatItem<T>> {
  const lookup: { [key: number]: T & { children?: T[] } } = {};
  const output: (T & { level: number })[] = [];

  initialArray.forEach((item) => {
    lookup[item.id] = { ...item, children: [] };
  });

  initialArray.forEach((item) => {
    const parentId = item[parentIdKey];
    if (typeof parentId === 'number' && item[parentIdKey] !== null && lookup[parentId]) {
      lookup[parentId].children?.push(lookup[item.id]);
    }
  });

  function addItemWithLevel(item: T & { children?: T[] }, level: number): void {
    const { children, ...rest } = item;

    output.push({ ...(rest as T), level });
    if (children && children.length) {
      children.forEach((child) => addItemWithLevel(child, level + 1));
    }
  }

  initialArray.forEach((item) => {
    if (item[parentIdKey] === null) {
      addItemWithLevel(lookup[item.id], 0);
    }
  });

  return output;
}

/**
 * Метод сортировки определяющий больше/меньше/равны переданные
 * аргументы так если бы они являлись строковыми представлениями дат.
 *
 * Работает со всеми строками которые распознаются JS-ом как даты,
 * т.е. `new Date(dateString)` возвращает валидную дату + русским форматом даты - ДД.ММ.ГГГГ
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function sortDateLikeValues(a: string, b: string): number {
  try {
    const [A, B] = [toDate(a), toDate(b)];
    return A < B ? 1 : -1;
  } catch (e) {
    // toDate в случае невалидной даты бросает исключение
    // если так, то порядок элементов не трогаем
    return 0;
  }
}

/**
 * Добавляем метод сортировки который пытается отсортировать колонку так,
 * как будто бы ее значения это строковое представление дат
 *
 * @param {QuasarTableColumn<T>} column
 * @returns {QuasarTableColumn<T>}
 * @constructor
 */
export function SortLikeDate<T extends object>(column: QuasarTableColumn<T>): QuasarTableColumn<T> {
  return {
    ...column,
    sort: sortDateLikeValues,
  };
}

export function getMyTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'utc';
}

/**
 * Метод позволяет обратиться к переменной среды, даже если он не объявлена.
 *
 * Обход ограничений самого квазара
 * https://quasar.dev/quasar-cli-vite/handling-process-env#wrong-usage
 *
 * @param {() => (string | undefined)} call
 * @returns {string | undefined}
 * @constructor
 */
export function SafeEnv(call: () => string | undefined) {
  try {
    return call();
  } catch (e) {
    if (e instanceof ReferenceError) {
      console.error(
        "You didn't set environment variable, please checkout callstack to find out which one!"
      );
      return undefined;
    }
    throw e;
  }
}

/**
 * Метод позволяет сериализовать объект, в объект с ключами name и value
 * где name - это ключ объекта, а value - это значение
 * ```js
 * const obj = {
 *    key1: 'value1',
 *    key2: {
 *      subKey1: 'subValue1',
 *      subKey2: 'subValue2',
 *    },
 *  };
 *
 *  const serializedObj = serializeJsObject(obj);
 *  console.log(serializedObj);
 *  // Output:
 *  // {
 *  //   key1: {
 *  //     name: 'key1',
 *  //     value: 'value1',
 *  //   },
 *  //   key2: {
 *  //     name: 'key2',
 *  //     value: {
 *  //       subKey1: {
 *  //         name: 'subKey1',
 *  //         value: 'subValue1',
 *  //       },
 *  //       subKey2: {
 *  //         name: 'subKey2',
 *  //         value: 'subValue2',
 *  //       },
 *  //     },
 *  //   },
 *  // }
 * ```
 * @param obj
 * @param nameKey
 * @param valueKey
 */
export function serializeJsObject(
  obj: object,
  nameKey: string = 'name',
  valueKey: string = 'value'
) {
  if (Array.isArray(obj)) {
    return obj.map((o) => serializeJsObject(o, nameKey, valueKey));
  }
  if (isObject(obj)) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        {
          [nameKey]: key,
          [valueKey]: serializeJsObject(value, nameKey, valueKey),
        },
      ])
    );
  }
  return obj;
}

/**
 * Текущая дата для отчётов
 *
 */
export function getCurrentDateTimeForReport() {
  const currentDateTime = new Date()
    .toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(/[,:. ]/g, '_')
    .replace(/(__)/g, '_');

  return currentDateTime;
}

/**
 *
 * @param timeout
 */
export async function wait(timeout?: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
}

/**
 * Дата на месячной давности от текущей
 */
export const getPreviousMonthDate = () => {
  const currentDate = new Date();
  const date = new Date(currentDate);

  date.setMonth(currentDate.getMonth() - 1);

  return formatToIsoDate(date);
};

/**
 * Lower case first letter of the string.
 *
 * If empty or null returns self.
 * @param str
 */
export function lcfirst(str: string | null) {
  if (!str) {
    return str;
  }
  return str[0].toLowerCase() + str.slice(1, str.length);
}

export function resolveComputable<V, T>(factory: Computable<V, T>, argument: T): V {
  if (typeof factory === 'function') {
    /**
     * без явного приведения TS определяет тип фабрики
     * ```
     * ((v: T) => V) | (V & Function)
     * ```
     * И не дает сделать ее вызов
     */
    const callable = factory as (v: T) => V;
    return callable(argument);
  }
  return factory;
}
